import * as angular from 'angular';
import * as moment from 'moment';
import { Component, ChangeDetectionStrategy, OnInit, OnDestroy, Input, Inject, ChangeDetectorRef } from '@angular/core';
import { downgradeComponent } from '@angular/upgrade/static';
import { Observable, of, Subscription } from 'rxjs';
import { BirdsEyeService } from '../../services/birds-eye.service';
import { BirdsEyeModel } from '../../../shared/models/birds-eye';
import Swal from 'sweetalert2';
import { catchError, map, switchMap, take } from 'rxjs/operators';
import { IUser } from 'app/shared/interfaces';
import { AuthenticationService, GeneralDataService } from 'app/shared/services';

enum BirdsEyeView {
  Weekly = 'weekly',
  Monthly = 'monthly',
}

@Component({
  templateUrl: './birds-eye.page.html',
  styleUrls: ['./birds-eye.page.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class BirdsEyePage implements OnInit, OnDestroy {
  public orderId: string;
  public model$: Observable<BirdsEyeModel>;
  public view = BirdsEyeView.Weekly;
  public sessions: { [key: number]: number[] } = {};
  public mode: 'schedule' | 'offer' = 'schedule';
  private originalModel: BirdsEyeModel; // Store original model from API
  public currentUser: IUser;
  public directScheduleInBirdseye: boolean;
  public isLoading: boolean = false;
  public keyProfession: any;
  private subscriptions = new Subscription();

  @Input() jobId: string;
  @Input() jobInstance: string;
  @Input() sessionId: string;
  @Input() closeCallback: any;

  BirdsEyeView = BirdsEyeView;

  constructor(
    private birdsEyeService: BirdsEyeService,
    private authenticationService: AuthenticationService,
    private generalDataService: GeneralDataService,
    @Inject('ModalService') private modalService: any,
    @Inject('ProviderManagerService') private providerManagerService: any,
    @Inject('$rootScope') private $rootScope: any,
    private cdr: ChangeDetectorRef
  ) {}

  async ngOnInit() {
    this.currentUser = this.authenticationService.getCurrentUser();
    this.directScheduleInBirdseye = this.currentUser.market_place_info ? this.currentUser.market_place_info.direct_schedule_in_birdseye : false;
    
    this.subscriptions.add(
      this.generalDataService.getKeyProfession().subscribe(profession => {
        this.keyProfession = { data_content: profession };
        this.cdr.markForCheck();
      })
    );
    
    this.model$ = this.birdsEyeService.fetchOrder(this.jobId, this.jobInstance);
    this.originalModel = await this.model$.pipe(take(1)).toPromise();
  }

  public getJobDates(model: BirdsEyeModel): string {
    const startDate = moment(model.job_start_date, 'YYYY-MM-DD');
    const endDate = moment(model.job_end_date, 'YYYY-MM-DD');

    let startText = startDate.format('MMM D');
    let endText = endDate.format('Do');

    if (!startDate.isSame(endDate, 'month')) {
      endText = endDate.format('MMM Do');
    }

    return `${startText}-${endText}`;
  }

  public setView(view: BirdsEyeView) {
    this.view = view;
  }

  public sessionsChanged(sessions: { [key: number]: number[] }) {
    this.sessions = { ...sessions };
  }

  public submit() {
    let rotated = {};
    Object.keys(this.sessions).forEach((sessionId) => {
      this.sessions[sessionId].forEach((providerId) => {
        const existing = rotated[providerId] || [];
        rotated[providerId] = [...existing, sessionId];
      });
    });

    of({})
      .pipe(
        switchMap(() => {
          if (this.mode === 'offer') {
            return this.birdsEyeService.offer(this.sessions);
          } else {
            return this.birdsEyeService.schedule(rotated);
          }
        }),
        take(1),
        map((res: any) => {
          if (res.success) {
            return Swal.fire({
              title: 'Success',
              text: res.message || 'Submit succesful.',
              icon: 'success',
              timer: 2000,
              showConfirmButton: false,
            }).then(() => {
              this.closeCallback && this.closeCallback.doClose();
            });
          } else {
            return Swal.fire({
              title: 'Error',
              text: res.message || 'There was an error submitting.',
              icon: 'error',
              timer: 2000,
              showConfirmButton: false,
            });
          }
        }),
        catchError((err) => {
          return Swal.fire({
            title: 'Error',
            text: err.message || 'There was an error submitting.',
            icon: 'error',
            timer: 2000,
            showConfirmButton: false,
          });
        })
      )
      .subscribe();
  }

  public submitEnabled() {
    return this.sessions && Object.keys(this.sessions).some((key) => this.sessions[key].length);
  }

  public goBack() {
    this.closeCallback && this.closeCallback.doClose();
  }

  ngOnDestroy() {
    this.subscriptions.unsubscribe();
  }

  /**
   * Normalizes worker status codes for popover display
   * Maps login_status/vetting_status to login_status_code/vetting_status_code
   */
  private normalizeWorkerStatusCodes(worker: any): any {
    return {
      ...worker,
      login_status_code: worker.login_status_code ?? worker.login_status ?? null,
      vetting_status_code: worker.vetting_status_code ?? worker.vetting_status ?? null
    };
  }

  /**
   * Fetches full provider details for workers to get their status codes
   */
  private async fetchWorkerDetails(workers: any[]): Promise<any[]> {
    if (workers.length === 0) return [];

    const providerDetailsPromises = workers.map((worker: any) =>
      this.providerManagerService.getProvider(worker.user_id).catch(() => null)
    );
    const providerDetails = await Promise.all(providerDetailsPromises);

    return workers.map((worker, index) => {
      const provider = providerDetails[index];
      if (provider) {
        return this.normalizeWorkerStatusCodes({
          ...worker,
          ...provider
        });
      }
      return this.normalizeWorkerStatusCodes(worker);
    });
  }

  public async selectWorkers() {
    if (!this.directScheduleInBirdseye) {
      return;
    }
    try {
      this.isLoading = true;

      // Get current and original workers
      const currentModel = await this.model$.pipe(take(1)).toPromise();
      const currentlySelectedWorkers = currentModel?.provider_list || [];
      const originalWorkers = this.originalModel?.provider_list || [];
      const currentlySelectedWorkerIds = new Set(currentlySelectedWorkers.map(w => w.provider_id));

      // Fetch and normalize all qualified workers from API
      const allQualifiedWorkersRaw = await this.providerManagerService.getQualifiedProviders({
        request_id: this.sessionId
      });
      const allQualifiedWorkers = allQualifiedWorkersRaw.map((worker: any) =>
        this.normalizeWorkerStatusCodes(worker)
      );

      // Build initial qualified workers list
      const qualifiedWorkers = allQualifiedWorkers.map(worker => ({
        ...worker,
        selected: false
      }));
      const qualifiedWorkerIds = new Set(qualifiedWorkers.map(w => w.provider_id));

      // Collect existing workers not in qualified list
      const workersNeedingDetails: any[] = [];
      const addWorkerIfMissing = (worker: any) => {
        if (!qualifiedWorkerIds.has(worker.provider_id) &&
            !workersNeedingDetails.some(w => w.provider_id === worker.provider_id)) {
          workersNeedingDetails.push(worker);
        }
      };

      originalWorkers.forEach(addWorkerIfMissing);
      currentlySelectedWorkers.forEach(addWorkerIfMissing);

      // Fetch details for existing workers and add to qualified list
      if (workersNeedingDetails.length > 0) {
        const workersWithDetails = await this.fetchWorkerDetails(workersNeedingDetails);
        workersWithDetails.forEach(worker => {
          qualifiedWorkers.push({
            ...worker,
            selected: false
          });
        });
      }

      // Create picklist with selection flags
      const pickList = qualifiedWorkers.map(worker => ({
        ...worker,
        selected: currentlySelectedWorkerIds.has(worker.provider_id)
      }));

      // Create a new AngularJS scope for this modal instance
      const modalScope = this.$rootScope.$new();

      // Set required properties on the AngularJS scope
      modalScope.pickList = pickList;
      modalScope.qualifiedWorkers = qualifiedWorkers;
      modalScope.offerWorkers = currentlySelectedWorkers.length;

      modalScope.jobType = {
        selection: 'escalation',
        bundledPickList: false,
        pickListRequested: true, 
      };

      modalScope.applyAttrib = {
        value: true
      };
      this.isLoading = false;
      this.cdr.markForCheck();
      // Open the modal with the newly created scope
      const modalInstance = this.modalService.open({
        controller: 'ModalPickListControler',
        templateUrl: 'app/views/schedule/pickListModal.html',
        scope: modalScope,
        backdrop: 'static',
        windowClass: 'pick-list-modal',
        resolve: {
          pickList: () => modalScope.pickList || [],
          qualifiedWorkers: () => modalScope.qualifiedWorkers || [],
          offerNumber: () =>  0,
        },
      });

      modalInstance.result.then(async (result) => {
        if (result) {
          // Note: No need to normalize status codes here - workers already have them from above
          // and they're only needed for display in the modal (which already happened)
          const selectedWorkers = result.pickList.filter(x => x.selected);
          
          // Transform selected workers to match the provider_list shape from the model
          const updatedProviderList = selectedWorkers.map(worker => ({
            name: worker.full_name || worker.name,
            profile_picture: worker.profile_picture || null,
            provider_id: worker.provider_id,
            user_id: worker.user_id
          }));
          
          // Get the provider IDs from selected workers
          const selectedProviderIds = selectedWorkers.map(worker => worker.provider_id);
          
          // Identify which workers are new (from qualified API) vs original
          const originalWorkerIds = new Set(originalWorkers.map(w => w.provider_id));
          const newWorkerIds = selectedProviderIds.filter(id => !originalWorkerIds.has(id));
          
          // Create updated model with new provider_list and session_list
          // Use originalModel as base to preserve all original data
          const updatedModel = {
            ...this.originalModel,
            provider_list: updatedProviderList,
            // Update each session's provider_ids array:
            // - Keep original workers' session assignments from originalModel
            // - Add new workers to all sessions
            session_list: this.originalModel.session_list.map(session => {
              const originalSessionProviderIds = session.provider_ids || [];
              
              const keptOriginalWorkers = originalSessionProviderIds.filter(id => 
                selectedProviderIds.includes(id)
              );
              
              // Combine kept original workers with new workers
              const updatedProviderIds = [...new Set([...keptOriginalWorkers, ...newWorkerIds])];
              
              return {
                ...session,
                provider_ids: updatedProviderIds
              };
            })
          };
          
          this.model$ = of(updatedModel);
          
          this.cdr.markForCheck();
          
          await Swal.fire({
            title: 'Workers Added!',
            text: 'Workers have been added to the provider list and sessions',
            icon: 'success',
            timer: 2000,
            showConfirmButton: false,
          });
        }
      });
    } catch (error) {
      this.isLoading = false;
      await Swal.fire({
        title: 'Error',
        text: error?.message || 'Failed to open pick list',
        icon: 'error',
        timer: 2000,
        showConfirmButton: false,
      });
    }
  }
}

angular.module('ServetureWebApp').directive('birdsEye', downgradeComponent({ component: BirdsEyePage }));
